import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../api/http.ts';
import { formatBytes } from '../lib/format.ts';
import { FileIcon } from '../components/files/FileIcon.tsx';
import { IconCloud, IconDownload, IconLock } from '../lib/icons.tsx';

interface Meta {
  type: 'file' | 'folder';
  name: string;
  size: number;
  mime: string | null;
  preview: string;
  requires_password: boolean;
  allow_download: boolean;
  expires_at: string | null;
  owner: string;
}

interface Entry { name: string; type: string; size: number; path: string; mime: string | null }

export function PublicLinkPage() {
  const { token = '' } = useParams();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadMeta = useCallback(async (lt?: string | null): Promise<void> => {
    try {
      const query = lt ? `?lt=${encodeURIComponent(lt)}` : '';
      const res = await fetch(`${API}/public/${token}${query}`);
      const body = (await res.json()) as Meta & { error?: { message: string } };
      if (!res.ok) { setError(body.error?.message ?? '링크를 열 수 없습니다'); return; }
      setMeta(body);
    } catch {
      setError('링크를 불러오지 못했습니다');
    }
  }, [token]);

  useEffect(() => { void loadMeta(null); }, [loadMeta]);

  const loadFolder = useCallback(async (lt: string | null): Promise<void> => {
    const query = lt ? `?lt=${encodeURIComponent(lt)}` : '';
    const res = await fetch(`${API}/public/${token}/list${query}`);
    if (res.ok) setEntries(((await res.json()) as { items: Entry[] }).items);
  }, [token]);

  useEffect(() => {
    if (meta?.type === 'folder' && !meta.requires_password) void loadFolder(null);
  }, [meta, loadFolder]);

  const unlock = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API}/public/${token}/auth`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const body = (await res.json()) as { link_token?: string; error?: { message: string } };
      if (!res.ok || !body.link_token) {
        setError(body.error?.message ?? '비밀번호가 올바르지 않습니다');
        return;
      }
      setLinkToken(body.link_token);
      // 인증 토큰을 함께 보내야 가려져 있던 이름·크기가 드러난다
      await loadMeta(body.link_token);
      await loadFolder(body.link_token);
    } finally {
      setBusy(false);
    }
  };

  const downloadHref = (path = ''): string => {
    const params = new URLSearchParams();
    if (path) params.set('path', path);
    if (linkToken) params.set('lt', linkToken);
    const query = params.toString();
    return `${API}/public/${token}/download${query ? `?${query}` : ''}`;
  };

  if (error && !meta) {
    return (
      <div className="public">
        <div className="public__card">
          <div className="auth__brand"><IconCloud size={24} /> YumSync</div>
          <p className="error-text" style={{ textAlign: 'center' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className="public"><span className="spinner" /></div>;
  }

  const locked = meta.requires_password && !linkToken;

  return (
    <div className="public">
      <div className="public__card">
        <div className="auth__brand"><IconCloud size={24} /> YumSync</div>

        {locked ? (
          <form onSubmit={(e) => { void unlock(e); }}>
            <p className="modal__text row"><IconLock size={16} /> 비밀번호로 보호된 링크입니다.</p>
            <div className="field">
              <label htmlFor="pw">비밀번호</label>
              <input
                id="pw" className="input" type="password" autoFocus required
                value={password} onChange={(e) => { setPassword(e.target.value); }}
              />
            </div>
            {error && <p className="error-text">{error}</p>}
            <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
              {busy ? <span className="spinner" /> : '열기'}
            </button>
          </form>
        ) : (
          <>
            <div className="public__file">
              <FileIcon node={{ type: meta.type, name: meta.name, mime: meta.mime }} size={40} />
              <div className="spacer">
                <div className="public__name">{meta.name}</div>
                <div className="muted">
                  {meta.type === 'folder' ? '폴더' : formatBytes(meta.size)}
                  {meta.owner && ` · ${meta.owner} 님이 공유`}
                </div>
              </div>
              {meta.type === 'file' && meta.allow_download && (
                <a className="btn btn--primary" href={downloadHref()}>
                  <IconDownload size={16} /> 다운로드
                </a>
              )}
            </div>

            {meta.type === 'folder' && (
              <div className="table">
                {entries.map((entry) => (
                  <div key={entry.path} className="frow" style={{ gridTemplateColumns: '1fr 110px 100px' }}>
                    <div className="frow__name">
                      <FileIcon node={{ type: entry.type as 'file' | 'folder', name: entry.name, mime: entry.mime ?? null }} />
                      <span className="frow__label">{entry.name}</span>
                    </div>
                    <div className="frow__meta">
                      {entry.type === 'folder' ? '—' : formatBytes(entry.size)}
                    </div>
                    <div>
                      {entry.type === 'file' && meta.allow_download && (
                        <a className="btn btn--sm" href={downloadHref(entry.path)}>다운로드</a>
                      )}
                    </div>
                  </div>
                ))}
                {entries.length === 0 && <p className="muted">폴더가 비어 있습니다.</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
